import { Page } from "@commerce/components/page";
import {
	InstallScreen,
	InstallSkeleton,
} from "@commerce/install/InstallScreen";
import {
	embedSettingsQuery,
	installStatusQuery,
	launchKeysQuery,
	sdkReleasesQuery,
	validateInstallSearch,
} from "@commerce/install/meta";
import { isOrganizationAdmin } from "@repo/auth/lib/helper";
import { config } from "@repo/config";
import { AdminOnlyNotice } from "@saas/settings/components/SettingsLayout";
import { AsyncBoundary } from "@shared/components/AsyncBoundary";
import { dehydrateForLoader, getServerQueryClient } from "@shared/lib/server";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute(
	"/_saas/app/_org/$organizationSlug/install",
)({
	validateSearch: validateInstallSearch,
	beforeLoad: ({ context }) => ({
		isOrganizationAdmin: isOrganizationAdmin(
			context.organization,
			context.session.user,
		),
		platformAdmin: context.session.user.role === "admin",
	}),
	// Switching platforms only changes the search; every platform's data is loaded once.
	shouldReload: false,
	loader: async ({ context }) => {
		const organizationId = context.organization.id;
		const queryClient = getServerQueryClient();
		if (context.isOrganizationAdmin) {
			await Promise.all([
				queryClient.ensureQueryData(embedSettingsQuery(organizationId)),
				queryClient.ensureQueryData(launchKeysQuery(organizationId)),
				queryClient.ensureQueryData(sdkReleasesQuery()),
				// Also picks the platform to open without a flash: the one seen last.
				queryClient.ensureQueryData(installStatusQuery(organizationId)),
			]);
		}
		return {
			organizationId,
			dehydratedState: dehydrateForLoader(queryClient),
		};
	},
	head: () => ({ meta: [{ title: `Install - ${config.appName}` }] }),
	component: InstallPage,
});

function InstallPage() {
	const { isOrganizationAdmin, platformAdmin } = Route.useRouteContext();
	const { organizationId, dehydratedState } = Route.useLoaderData();

	if (!isOrganizationAdmin) {
		return (
			<Page width="narrow">
				<AdminOnlyNotice what="install settings" />
			</Page>
		);
	}
	return (
		<AsyncBoundary
			fallback={<InstallSkeleton />}
			dehydratedState={dehydratedState}
			errorFallback="inline"
		>
			<InstallScreen
				organizationId={organizationId}
				platformAdmin={platformAdmin}
			/>
		</AsyncBoundary>
	);
}
